import { FormSectionSimple } from 'components/layout/Section/FormSectionSimple';
import { NsKey } from 'enums/NsKey';
import { InputFactory } from 'factories/InputFactory';
import { AddressFormFieldNames } from 'shared/enums/addressFormFieldNames';
import { NaturalPersonFormFieldNames } from 'features/new-actor/enums/naturalPersonFormFieldNames';
import { PersonalInformationFieldNames } from 'features/new-actor/enums/personalInformationFieldNames';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { useCallback, useMemo } from 'react';
import {
  BorrowerInsuredFormFieldNames,
  BorrowerNaturalFormValues
} from '../borrowerInsuredForm';
import { BorrowerBaseLayout } from '../shared/BorrowerBaseLayout';
import { borrowerNaturalInputsConfig } from './borrowerNatural.inputs';

interface BorrowerNaturalLayoutProps {
  isEdit?: boolean;
  initialTab?: number | string;
}

export const BorrowerNaturalLayout: React.FC<BorrowerNaturalLayoutProps> = ({
  isEdit = false,
  initialTab
}) => {
  const { t } = useDynamicTranslation([NsKey.Borrower, NsKey.ActorForm]);

  const naturalPersonDisabledFields: Array<
    Exclude<keyof BorrowerNaturalFormValues, 'id'>
  > = useMemo(
    () => [
      PersonalInformationFieldNames.LastName,
      PersonalInformationFieldNames.FirstName,
      PersonalInformationFieldNames.Civility
    ],
    []
  );

  const renderField = useCallback(
    (fieldName: any) => {
      return (
        <InputFactory<Exclude<keyof BorrowerNaturalFormValues, 'id'>>
          config={borrowerNaturalInputsConfig}
          fieldName={fieldName}
          disabledFields={isEdit ? naturalPersonDisabledFields : undefined}
        />
      );
    },
    [isEdit, naturalPersonDisabledFields]
  );

  return (
    <BorrowerBaseLayout initialTab={initialTab}>
      <div className="font-lato flex flex-col gap-4">
        <FormSectionSimple title={t('ACTOR_FORM.LABELS.CONTACT_SECTION')}>
          <>
            <div className="w-full col-span-6 flex gap-4">
              <div className="w-32">
                {renderField(PersonalInformationFieldNames.Civility)}
              </div>
              <div className="flex-grow">
                {renderField(PersonalInformationFieldNames.LastName)}
              </div>
            </div>
            <div className="col-span-3">
              {renderField(PersonalInformationFieldNames.FirstName)}
            </div>
            <div className="col-span-3">
              {renderField(PersonalInformationFieldNames.BirthName)}
            </div>
            <div className="col-span-3">
              {renderField(PersonalInformationFieldNames.BirthCountry)}
            </div>
            <div className="col-span-3">
              {renderField(PersonalInformationFieldNames.Nationality)}
            </div>
            <div className="col-span-3">
              {renderField(PersonalInformationFieldNames.BirthDate)}
            </div>
            <div className="col-span-3">
              {renderField(PersonalInformationFieldNames.Email)}
            </div>
            <div className="col-span-3">
              {renderField(PersonalInformationFieldNames.Phone)}
            </div>
            <div className="col-span-3">
              {renderField(NaturalPersonFormFieldNames.TaxResidenceCountryId)}
            </div>
            <div className="col-span-3">
              {renderField(BorrowerInsuredFormFieldNames.Job)}
            </div>
            <div className="col-span-3">
              {renderField(BorrowerInsuredFormFieldNames.ProspectType)}
            </div>
            <div className="col-span-3">
              {renderField(PersonalInformationFieldNames.IsPPE)}
            </div>
            <div className="col-span-3">
              {renderField(BorrowerInsuredFormFieldNames.IsFPE)}
            </div>
          </>
        </FormSectionSimple>
        <FormSectionSimple title={t('ACTOR_FORM.LABELS.COORDINATE_SECTION')}>
          <>
            <div className="col-span-3">
              {renderField(AddressFormFieldNames.Address)}
            </div>
            <div className="col-span-3">
              {renderField(AddressFormFieldNames.AdditionalAddress)}
            </div>
            <div className="col-span-3">
              {renderField(AddressFormFieldNames.PostalCode)}
            </div>
            <div className="col-span-3">
              {renderField(AddressFormFieldNames.City)}
            </div>
            <div className="col-span-3">
              {renderField(AddressFormFieldNames.Country)}
            </div>
          </>
        </FormSectionSimple>
      </div>
    </BorrowerBaseLayout>
  );
};
